import React, { useState } from 'react';
import api from '../utils/api';
import './IssueCertificates.css';

export interface Certificate {
  firstName: string;
  lastName: string;
  organization: string;
  courseName: string;
  issueDate: string;
  duration?: string;
  city?: string;
}

interface IssueProps {
  onIssued?: () => void;
  onDraftChange?: (draft: Certificate) => void;
}

const IssueCertificate: React.FC<IssueProps> = ({ onIssued, onDraftChange }) => {
  const [formData, setFormData] = useState<Certificate>({
    firstName: '',
    lastName: '',
    organization: '',
    courseName: '',
    issueDate: '',
    duration: '',
    city: '',
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [isError, setIsError] = useState<boolean>(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = { ...formData, [e.target.name]: e.target.value };
    setFormData(next);
    onDraftChange?.(next);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setIsError(false);
    try {
      await api.post('/v1/vitrine/certificates/issue', formData);
      setMessage('Certificate issued successfully');
      onIssued?.();
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Failed to issue certificate');
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="glass-card">
      <h2>Single Issuance</h2>
      <form onSubmit={handleSubmit} className="issue-form">
        <div className="form-group">
          <label>First Name</label>
          <input type="text" name="firstName" placeholder="Enter first name" value={formData.firstName} onChange={handleChange} className="custom-input" required />
        </div>
        <div className="form-group">
          <label>Last Name</label>
          <input type="text" name="lastName" placeholder="Enter last name" value={formData.lastName} onChange={handleChange} className="custom-input" required />
        </div>
        <div className="form-group">
          <label>Organization</label>
          <input type="text" name="organization" placeholder="Enter organization" value={formData.organization} onChange={handleChange} className="custom-input" required />
        </div>
        <div className="form-group">
          <label>Course Name</label>
          <input type="text" name="courseName" placeholder="Enter course name" value={formData.courseName} onChange={handleChange} className="custom-input" required />
        </div>
        <div className="form-group">
          <label>Issue Date</label>
          <input type="date" name="issueDate" value={formData.issueDate} onChange={handleChange} className="custom-input" required />
        </div>
        <div className="form-group">
          <label>Duration (Hours)</label>
          <input type="text" name="duration" placeholder="e.g. 02" value={formData.duration} onChange={handleChange} className="custom-input" />
        </div>
        <div className="form-group">
          <label>City</label>
          <input type="text" name="city" placeholder="e.g. Tlemcen" value={formData.city} onChange={handleChange} className="custom-input" />
        </div>
        <button type="submit" className="submit-btn" disabled={loading}>
          {loading ? 'Issuing Certificate...' : 'Issue Certificate'}
        </button>
      </form>
      {message && (
        <div className={`message ${isError ? 'error' : 'success'}`}>
          {message}
        </div>
      )}
    </div>
  );
};


const CsvUpload: React.FC<{ onIssued?: () => void }> = ({ onIssued }) => {
  const [file, setFile] = useState<File | null>(null);
  const [message, setMessage] = useState<string>('');
  const [isError, setIsError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFile(e.target.files[0]);
      setMessage('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setMessage('Please select a CSV file.');
      setIsError(true);
      return;
    }

    const formData = new FormData();
    formData.append('csv', file);

    setLoading(true);
    setMessage('');
    setIsError(false);

    try {
      const response = await api.post(
        '/v1/vitrine/certificates/import',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      const msg = response.data.message || `Successfully issued ${response.data.issued ?? 0} certificate(s).`;
      setMessage(msg);
      onIssued?.();
    } catch (error: any) {
      const data = error.response?.data;
      let msg = data?.message || 'Error uploading CSV file.';
      if (data?.errors && Array.isArray(data.errors)) {
        msg += ' ' + data.errors.map((e: { row: number; message: string }) => `Row ${e.row}: ${e.message}`).join('; ');
      }
      setMessage(msg);
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="glass-card">
      <h2>Bulk Issuance</h2>
      <p className="csv-format-hint">
        Use the same data as single issuance. CSV must have a header row with columns: <strong>firstName</strong>, <strong>lastName</strong>, <strong>organization</strong>, <strong>courseName</strong>, <strong>issueDate</strong> (YYYY-MM-DD). Optional: <strong>duration</strong>, <strong>city</strong>.
      </p>
      <a href={`${process.env.PUBLIC_URL || ''}/certificates-bulk-example.csv`} download className="csv-download-link">
        Download example CSV
      </a>
      <form onSubmit={handleSubmit} className="issue-form">
        <label className="csv-upload-container">
          <p>Click to select or drag & drop CSV file</p>
          <input type="file" accept=".csv" onChange={handleFileChange} style={{ display: 'none' }} />
          {file && <div className="file-info">Selected: <strong>{file.name}</strong></div>}
        </label>

        <button type="submit" className="submit-btn" disabled={loading || !file}>
          {loading ? 'Uploading CSV...' : 'Start Bulk Import'}
        </button>
      </form>
      {message && (
        <div className={`message ${isError ? 'error' : 'success'}`}>
          {message}
        </div>
      )}
    </div>
  );
};

export { IssueCertificate, CsvUpload };

